import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from './lib/queryClient';
import { AuthProvider, useAuth } from './authContext';
import { TooltipProvider } from './components/ui/tooltip';
import Layout from './components/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import ProjectLayout from './pages/Project/Layout';
import ProjectHome from './pages/Project/Home';
import Workspace from './pages/Workspace';
import ProjectTasks from './pages/Project/Tasks';
import ProjectFiles from './pages/Project/Files';
import ProjectSettings from './pages/Project/Settings'; // Import Settings
import ProjectChat from './pages/Project/Chat';
import ProjectOnboarding from './pages/Project/Onboarding';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import InviteHandler from './pages/InviteHandler';
import SharedProjectLayout from './pages/SharedProject/Layout';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <div className="text-white">Loading...</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  return children;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          {/* One provider for every <Tip> in the app.
              - skipDelayDuration: sweeping across a dense toolbar shows the
                next tooltip instantly instead of re-waiting 250ms per icon.
              - disableHoverableContent: the toolbars sit directly above their
                content, so a tooltip that stays open under the pointer would
                cover the thing being previewed.
              Zen mode portals to document.body, but React context follows the
              component tree rather than the DOM, so it is covered too. */}
          <TooltipProvider delayDuration={250} skipDelayDuration={400} disableHoverableContent>
            <Layout>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />

                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <Dashboard />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/settings"
                  element={
                    <PrivateRoute>
                      <Settings />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/profile"
                  element={
                    <PrivateRoute>
                      <Profile />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/project/:projectId/onboarding"
                  element={
                    <PrivateRoute>
                      <ProjectOnboarding />
                    </PrivateRoute>
                  }
                />

                <Route
                  path="/project/:projectId"
                  element={
                    <PrivateRoute>
                      <ProjectLayout />
                    </PrivateRoute>
                  }
                >
                  <Route index element={<Navigate to="home" replace />} />
                  <Route path="home" element={<ProjectHome />} />
                  <Route path="chat" element={<ProjectChat />} />
                  <Route path="files" element={<ProjectFiles />} />
                  <Route path="editor" element={<Workspace />} />
                  <Route path="editor/:fileId" element={<Workspace />} />
                  <Route path="tasks" element={<ProjectTasks />} />
                  <Route path="settings" element={<ProjectSettings />} />
                </Route>

                <Route path="/invite/:token" element={<InviteHandler />} />

                <Route path="/s/:token" element={<SharedProjectLayout />}>
                  <Route index element={<Navigate to="home" replace />} />
                  <Route path="home" element={<ProjectHome />} />
                  <Route path="chat" element={<ProjectChat />} />
                  <Route path="tasks" element={<ProjectTasks />} />
                  <Route path="files" element={<ProjectFiles />} />
                  <Route path="editor" element={<Workspace />} />
                  <Route path="editor/:fileId" element={<Workspace />} />
                </Route>

                <Route path="/" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Layout>
          </TooltipProvider>
        </AuthProvider>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
